// ============================================================
// src/pages/IPManagementPage.jsx  [Supervisor]
// Fully responsive — mobile first
// ============================================================
import React, { useState, useEffect, useCallback } from 'react';
import { getWhitelist, addIP, removeIP, updateIP, getAccessLog, getIPStats, grantEmergency } from '../api/ipApi';
import { getEmployees } from '../api/employeeApi';
import { Toast, Spinner } from '../components/UI';
import { useToast } from '../hooks/useToast';

// ── Responsive hook ──────────────────────────────────────────
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);
  return isMobile;
}

export default function IPManagementPage() {
  const { toast, showToast, clearToast } = useToast();
  const isMobile = useIsMobile();

  const [whitelist,  setWhitelist]  = useState([]);
  const [accessLog,  setAccessLog]  = useState([]);
  const [stats,      setStats]      = useState({});
  const [mechanics,  setMechanics]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [logType,    setLogType]    = useState('');

  const [addOpen,    setAddOpen]    = useState(false);
  const [ipForm,     setIpForm]     = useState({ ip_value: '', ip_type: 'single', location_label: '' });
  const [addSaving,  setAddSaving]  = useState(false);

  const [editOpen,   setEditOpen]   = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm,   setEditForm]   = useState({ ip_value: '', ip_type: 'single', location_label: '' });
  const [editSaving, setEditSaving] = useState(false);

  const [emergOpen,   setEmergOpen]   = useState(false);
  const [emergForm,   setEmergForm]   = useState({ employee_id: '', ip_address: '', reason: '', hours: '8' });
  const [emergSaving, setEmergSaving] = useState(false);

  const iUpd  = k => e => setIpForm(f   => ({ ...f, [k]: e.target.value }));
  const edUpd = k => e => setEditForm(f  => ({ ...f, [k]: e.target.value }));
  const eUpd  = k => e => setEmergForm(f => ({ ...f, [k]: e.target.value }));

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [wlData, logData, statData, mechData] = await Promise.all([
        getWhitelist(),
        getAccessLog({ type: logType, limit: 10 }),
        getIPStats(),
        getEmployees(),
      ]);
      setWhitelist(wlData);
      setAccessLog(logData);
      setStats(statData);
      setMechanics(mechData);
    } catch (err) {
      showToast(err.message || 'Failed to load', 'error');
    } finally {
      setLoading(false);
    }
  }, [logType, showToast]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  async function handleAddIP(e) {
    e.preventDefault();
    if (!ipForm.ip_value.trim()) { showToast('IP value required', 'error'); return; }
    setAddSaving(true);
    try {
      await addIP({ ip_value: ipForm.ip_value.trim(), ip_type: ipForm.ip_type, location_label: ipForm.location_label.trim() });
      showToast('IP added ✓', 'success');
      setAddOpen(false);
      setIpForm({ ip_value: '', ip_type: 'single', location_label: '' });
      fetchAll();
    } catch (err) { showToast(err.message || 'Failed to add', 'error'); }
    finally { setAddSaving(false); }
  }

  function openEdit(ip) {
    setEditTarget(ip);
    setEditForm({ ip_value: ip.ip_value || '', ip_type: ip.ip_type || 'single', location_label: ip.location_label || '' });
    setEditOpen(true);
  }

  async function handleEditIP(e) {
    e.preventDefault();
    if (!editForm.ip_value.trim()) { showToast('IP value required', 'error'); return; }
    setEditSaving(true);
    try {
      await updateIP(editTarget.id, { ip_value: editForm.ip_value.trim(), ip_type: editForm.ip_type, location_label: editForm.location_label.trim() });
      showToast('IP updated ✓', 'success');
      setEditOpen(false); setEditTarget(null);
      fetchAll();
    } catch (err) { showToast(err.message || 'Failed to update', 'error'); }
    finally { setEditSaving(false); }
  }

  async function handleRemoveIP(id, label) {
    if (!window.confirm(`Remove "${label}" from whitelist?`)) return;
    try { await removeIP(id); showToast('Removed ✓', 'warning'); fetchAll(); }
    catch { showToast('Failed to remove', 'error'); }
  }

  async function handleEmergency(e) {
    e.preventDefault();
    if (!emergForm.employee_id || !emergForm.ip_address.trim()) { showToast('Employee and IP required', 'error'); return; }
    setEmergSaving(true);
    try {
      await grantEmergency({ employee_id: emergForm.employee_id, ip_address: emergForm.ip_address.trim(), reason: emergForm.reason.trim(), hours: emergForm.hours });
      showToast(`Emergency access granted for ${emergForm.hours}h ✓`, 'success');
      setEmergOpen(false);
      setEmergForm({ employee_id: '', ip_address: '', reason: '', hours: '8' });
    } catch (err) { showToast(err.message || 'Failed to grant', 'error'); }
    finally { setEmergSaving(false); }
  }

  function logMeta(eventType) {
    switch (eventType) {
      case 'login_success':   return { icon: '✓', color: '#16a34a', bg: '#dcfce7' };
      case 'login_failed':    return { icon: '✕', color: '#dc2626', bg: '#fee2e2' };
      case 'login_denied_ip': return { icon: '⊘', color: '#dc2626', bg: '#fee2e2' };
      case 'logout':          return { icon: '●', color: '#6b7280', bg: '#f3f4f6' };
      default:                return { icon: '✎', color: '#f59e0b', bg: '#fef9c3' };
    }
  }

  function fmtTime(val) {
    if (!val) return '—';
    return String(val).slice(0, 16).replace('T', ' ');
  }

  // ── Shared styles ──
  const inputStyle = {
    width: '100%', boxSizing: 'border-box',
    border: '1px solid #d1d5db', borderRadius: '6px',
    padding: '0.5rem 0.75rem', fontSize: '0.875rem',
    color: '#111827', background: '#fff', outline: 'none',
  };
  const labelStyle = {
    display: 'block', fontSize: '0.72rem', fontWeight: 600,
    color: '#6b7280', textTransform: 'uppercase',
    letterSpacing: '.05em', marginBottom: '0.3rem',
  };
  const panelCard = {
    background: '#fff', border: '1px solid #e5e7eb',
    borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,.05)',
    overflow: 'hidden',
  };

  // ── Modal shell ──
  function ModalShell({ title, onClose, children, footer, maxWidth = '420px' }) {
    return (
      <div style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        zIndex: 9999, display: 'flex', alignItems: 'center',
        justifyContent: 'center', padding: '1rem',
      }}>
        <div style={{
          background: '#fff', borderRadius: '10px',
          width: '100%', maxWidth,
          boxShadow: '0 24px 64px rgba(0,0,0,.22)',
          maxHeight: '90vh', overflowY: 'auto',
        }}>
          <div style={{
            padding: '1rem 1.25rem', borderBottom: '1px solid #e5e7eb',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            position: 'sticky', top: 0, background: '#fff', zIndex: 1,
          }}>
            <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#111827' }}>{title}</span>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '1.3rem', lineHeight: 1, padding: '0.2rem' }}>✕</button>
          </div>
          <div style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
            {children}
          </div>
          {footer && (
            <div style={{
              padding: '0.85rem 1.25rem', borderTop: '1px solid #e5e7eb',
              display: 'flex', justifyContent: 'flex-end', gap: '0.75rem',
              flexWrap: 'wrap',
              position: 'sticky', bottom: 0, background: '#fff',
            }}>
              {footer}
            </div>
          )}
        </div>
      </div>
    );
  }

  function CancelBtn({ onClick }) {
    return (
      <button type="button" onClick={onClick} style={{
        background: '#fff', border: '1px solid #d1d5db', borderRadius: '5px',
        padding: '0.5rem 1rem', fontSize: '0.8rem', fontWeight: 700,
        color: '#374151', cursor: 'pointer', flex: isMobile ? 1 : 'none',
      }}>CANCEL</button>
    );
  }

  function SubmitBtn({ label, saving, color = '#dc2626' }) {
    return (
      <button type="submit" disabled={saving} style={{
        background: color, border: 'none', borderRadius: '5px',
        padding: '0.5rem 1.1rem', fontSize: '0.8rem', fontWeight: 700,
        color: '#fff', cursor: saving ? 'not-allowed' : 'pointer',
        opacity: saving ? 0.7 : 1,
        flex: isMobile ? 1 : 'none',
      }}>{saving ? 'SAVING…' : label}</button>
    );
  }

  if (loading) return <div style={{ padding: '3rem', textAlign: 'center' }}><Spinner /></div>;

  return (
    <div style={{ padding: isMobile ? '1rem' : '1.5rem', background: '#f3f4f6', minHeight: '100vh' }}>

      {/* ── Page Header ── */}
      <div style={{
        display: 'flex', alignItems: 'flex-start',
        justifyContent: 'space-between',
        marginBottom: '1.25rem',
        flexWrap: 'wrap', gap: '0.75rem',
      }}>
        <div>
          <h1 style={{ fontSize: isMobile ? '1.25rem' : '1.6rem', fontWeight: 800, margin: 0, color: '#111827', letterSpacing: '.02em' }}>
            IP ACCESS MANAGEMENT
          </h1>
          <p style={{ margin: '0.2rem 0 0', fontSize: '0.82rem', color: '#6b7280' }}>
            Manage whitelisted office IP addresses
          </p>
        </div>
        <button onClick={() => setAddOpen(true)} style={{
          background: '#dc2626', border: 'none', borderRadius: '6px',
          padding: '0.55rem 1rem', fontSize: '0.8rem', fontWeight: 700,
          color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap',
          alignSelf: 'flex-start',
        }}>+ ADD IP</button>
      </div>

      {/* ── Stat Strip ── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3, 1fr)',
        gap: '0.75rem',
        marginBottom: '1.25rem',
      }}>
        {[
          { label: 'WHITELISTED IPS',       value: stats.total_whitelisted ?? 0, sub: 'Active entries',   accent: '#16a34a' },
          { label: 'ACCESS ATTEMPTS TODAY', value: stats.logins_today      ?? 0, sub: 'Total logins',     accent: '#2563eb' },
          { label: 'BLOCKED ATTEMPTS',      value: stats.blocked_today     ?? 0, sub: 'Unauthorized IPs', accent: '#dc2626' },
        ].map((s, i) => (
          <div key={s.label} style={{
            background: '#fff', border: '1px solid #e5e7eb',
            borderRadius: '7px', padding: '0.9rem 1rem',
            boxShadow: '0 1px 3px rgba(0,0,0,.05)',
            borderTop: `3px solid ${s.accent}`,
            // On mobile last stat spans full width
            gridColumn: isMobile && i === 2 ? '1 / -1' : undefined,
          }}>
            <div style={{ fontSize: '0.62rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: '0.35rem' }}>
              {s.label}
            </div>
            <div style={{ fontSize: isMobile ? '1.6rem' : '2rem', fontWeight: 800, color: '#111827', lineHeight: 1 }}>
              {s.value}
            </div>
            <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: '0.25rem' }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* ── Two-column layout ── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(340px, 1fr))',
        gap: '1rem',
        alignItems: 'start',
      }}>

        {/* ══ Whitelist Panel ══ */}
        <div style={panelCard}>
          <div style={{ padding: '0.85rem 1.25rem', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span>🔒</span>
            <span style={{ fontWeight: 700, fontSize: '0.82rem', color: '#111827', textTransform: 'uppercase', letterSpacing: '.04em' }}>
              Whitelisted IPs
            </span>
            <span style={{
              marginLeft: 'auto', background: '#f3f4f6', color: '#6b7280',
              fontSize: '0.7rem', fontWeight: 700, borderRadius: '99px',
              padding: '0.15rem 0.6rem',
            }}>{whitelist.length}</span>
          </div>

          {whitelist.length === 0 ? (
            <div style={{ padding: '2.5rem', textAlign: 'center', color: '#9ca3af', fontSize: '0.85rem' }}>
              No IPs whitelisted.
            </div>
          ) : whitelist.map((ip, idx) => (
            <div key={ip.id} style={{
              padding: isMobile ? '0.85rem 1rem' : '0.75rem 1.25rem',
              borderBottom: idx < whitelist.length - 1 ? '1px solid #f3f4f6' : 'none',
              background: '#fff',
              // On mobile stack vertically
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'flex-start' : 'center',
              justifyContent: 'space-between',
              gap: '0.6rem',
            }}
              onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: 'monospace', fontWeight: 700, color: '#16a34a', fontSize: '0.9rem' }}>
                  {ip.ip_value}
                </div>
                <div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: '0.15rem', display: 'flex', flexWrap: 'wrap', gap: '0.3rem', alignItems: 'center' }}>
                  <span style={{ background: '#f3f4f6', borderRadius: '4px', padding: '0.1rem 0.4rem', fontWeight: 600 }}>
                    {ip.ip_type}
                  </span>
                  {ip.location_label && (
                    <span>{ip.location_label}</span>
                  )}
                  <span style={{ color: '#d1d5db' }}>·</span>
                  <span>Added {String(ip.created_at || '').slice(0, 10)}</span>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0, width: isMobile ? '100%' : 'auto' }}>
                <button onClick={() => openEdit(ip)} style={{
                  flex: isMobile ? 1 : 'none',
                  background: '#fff', border: '1px solid #d1d5db', borderRadius: '5px',
                  padding: '0.35rem 0.75rem', fontSize: '0.75rem', fontWeight: 700,
                  color: '#374151', cursor: 'pointer',
                }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#374151'; e.currentTarget.style.background = '#f9fafb'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.background = '#fff'; }}
                >✏️ EDIT</button>

                <button onClick={() => handleRemoveIP(ip.id, ip.location_label || ip.ip_value)} style={{
                  flex: isMobile ? 1 : 'none',
                  background: '#fff', border: '1px solid #fca5a5', borderRadius: '5px',
                  padding: '0.35rem 0.75rem', fontSize: '0.75rem', fontWeight: 700,
                  color: '#dc2626', cursor: 'pointer',
                }}
                  onMouseEnter={e => e.currentTarget.style.background = '#fef2f2'}
                  onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                >🗑 REMOVE</button>
              </div>
            </div>
          ))}
        </div>

        {/* ══ Access Log Panel ══ */}
        <div style={panelCard}>
          <div style={{
            padding: '0.85rem 1.25rem', borderBottom: '1px solid #e5e7eb',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexWrap: 'wrap', gap: '0.5rem',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span>📋</span>
              <span style={{ fontWeight: 700, fontSize: '0.82rem', color: '#111827', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                Recent Access Log
              </span>
            </div>
            <select value={logType} onChange={e => setLogType(e.target.value)} style={{
              border: '1px solid #d1d5db', borderRadius: '5px',
              padding: '0.3rem 0.5rem', fontSize: '0.75rem',
              color: '#374151', background: '#fff', outline: 'none', cursor: 'pointer',
              maxWidth: '140px',
            }}>
              <option value="">All Events</option>
              <option value="login_success">Login Success</option>
              <option value="login_failed">Login Failed</option>
              <option value="login_denied_ip">IP Denied</option>
              <option value="logout">Logout</option>
            </select>
          </div>

          <div style={{ maxHeight: isMobile ? 'none' : '480px', overflowY: 'auto' }}>
            {accessLog.length === 0 ? (
              <div style={{ padding: '2.5rem', textAlign: 'center', color: '#9ca3af', fontSize: '0.85rem' }}>No log entries.</div>
            ) : accessLog.slice(0, 50).map((log, idx) => {
              const meta = logMeta(log.event_type);
              return (
                <div key={log.id} style={{
                  display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
                  padding: isMobile ? '0.75rem 1rem' : '0.8rem 1.25rem',
                  borderBottom: idx < Math.min(accessLog.length, 50) - 1 ? '1px solid #f3f4f6' : 'none',
                  background: '#fff',
                }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                  onMouseLeave={e => e.currentTarget.style.background = '#fff'}>

                  <div style={{
                    width: '24px', height: '24px', borderRadius: '50%',
                    background: meta.bg, flexShrink: 0, marginTop: '0.1rem',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.65rem', color: meta.color, fontWeight: 700,
                  }}>{meta.icon}</div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.82rem', color: '#111827', lineHeight: 1.5 }}>
                      <span style={{ fontWeight: 700, color: '#dc2626' }}>
                        {log.emp_name || log.ip_address || 'Unknown'}
                      </span>{' '}
                      <span style={{ color: '#374151' }}>
                        {log.event_type === 'login_success'    ? `logged in from ${log.ip_address}`
                        : log.event_type === 'login_denied_ip' ? `— access denied from ${log.ip_address}`
                        : log.event_type === 'login_failed'    ? `— login failed from ${log.ip_address}`
                        : log.event_type === 'logout'          ? 'logged out'
                        : (log.event_type || '').replace(/_/g, ' ')}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: '0.15rem' }}>
                      {fmtTime(log.created_at)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ══ Add IP Modal ══ */}
      {addOpen && (
        <ModalShell title="➕ Add IP to Whitelist" onClose={() => setAddOpen(false)}
          footer={
            <form id="add-ip-form" onSubmit={handleAddIP} style={{ display: 'contents' }}>
              <CancelBtn onClick={() => setAddOpen(false)} />
              <SubmitBtn label="ADD IP" saving={addSaving} />
            </form>
          }>
          <form id="add-ip-form" onSubmit={handleAddIP} style={{ display: 'contents' }}>
            <div>
              <label style={labelStyle}>IP Value *</label>
              <input style={inputStyle} type="text" value={ipForm.ip_value} onChange={iUpd('ip_value')}
                placeholder="192.168.1.1 or 192.168.1.0/24" required autoFocus />
            </div>
            <div>
              <label style={labelStyle}>Type *</label>
              <select style={inputStyle} value={ipForm.ip_type} onChange={iUpd('ip_type')}>
                <option value="single">Single IP</option>
                <option value="subnet">Subnet (CIDR)</option>
                <option value="range">Range (x.x.x.x-y.y.y.y)</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Location Label</label>
              <input style={inputStyle} type="text" value={ipForm.location_label} onChange={iUpd('location_label')}
                placeholder="e.g. Shop Floor Terminal 1" />
            </div>
          </form>
        </ModalShell>
      )}

      {/* ══ Edit IP Modal ══ */}
      {editOpen && editTarget && (
        <ModalShell title="✏️ Edit IP Address" onClose={() => setEditOpen(false)}
          footer={
            <form id="edit-ip-form" onSubmit={handleEditIP} style={{ display: 'contents' }}>
              <CancelBtn onClick={() => setEditOpen(false)} />
              <SubmitBtn label="SAVE CHANGES" saving={editSaving} color="#2563eb" />
            </form>
          }>
          <form id="edit-ip-form" onSubmit={handleEditIP} style={{ display: 'contents' }}>
            {/* Current IP badge */}
            <div style={{
              background: '#f0fdf4', border: '1px solid #bbf7d0',
              borderRadius: '6px', padding: '0.65rem 0.9rem',
              fontSize: '0.78rem', color: '#15803d',
              display: 'flex', alignItems: 'center', gap: '0.5rem',
            }}>
              <span>🔒</span>
              <span>Editing: <strong style={{ fontFamily: 'monospace' }}>{editTarget.ip_value}</strong></span>
            </div>
            <div>
              <label style={labelStyle}>IP Value *</label>
              <input style={inputStyle} type="text" value={editForm.ip_value} onChange={edUpd('ip_value')}
                placeholder="192.168.1.1 or 192.168.1.0/24" required autoFocus />
            </div>
            <div>
              <label style={labelStyle}>Type *</label>
              <select style={inputStyle} value={editForm.ip_type} onChange={edUpd('ip_type')}>
                <option value="single">Single IP</option>
                <option value="subnet">Subnet (CIDR)</option>
                <option value="range">Range (x.x.x.x-y.y.y.y)</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Location Label</label>
              <input style={inputStyle} type="text" value={editForm.location_label} onChange={edUpd('location_label')}
                placeholder="e.g. Shop Floor Terminal 1" />
            </div>
          </form>
        </ModalShell>
      )}

      {/* ══ Emergency Access Modal ══ */}
      {emergOpen && (
        <ModalShell title="🚨 Grant Emergency Access" onClose={() => setEmergOpen(false)}
          footer={
            <form id="emerg-form" onSubmit={handleEmergency} style={{ display: 'contents' }}>
              <CancelBtn onClick={() => setEmergOpen(false)} />
              <SubmitBtn label="GRANT ACCESS" saving={emergSaving} />
            </form>
          }>
          <form id="emerg-form" onSubmit={handleEmergency} style={{ display: 'contents' }}>
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '6px', padding: '0.7rem 0.9rem', fontSize: '0.8rem', color: '#92400e' }}>
              ⚠ Emergency access bypasses IP restriction for the selected mechanic for a limited time.
            </div>
            <div>
              <label style={labelStyle}>Mechanic *</label>
              <select style={inputStyle} value={emergForm.employee_id} onChange={eUpd('employee_id')} required>
                <option value="">— Select Mechanic —</option>
                {mechanics.map(m => (
                  <option key={m.id} value={m.id}>{m.ee_number} – {m.last_name}, {m.first_name}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>IP Address *</label>
              <input style={inputStyle} type="text" value={emergForm.ip_address} onChange={eUpd('ip_address')} placeholder="e.g. 10.0.5.22" required />
            </div>
            <div>
              <label style={labelStyle}>Duration (hours) *</label>
              <select style={inputStyle} value={emergForm.hours} onChange={eUpd('hours')}>
                {[1, 2, 4, 8, 12, 24].map(h => (
                  <option key={h} value={h}>{h} hour{h > 1 ? 's' : ''}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Reason</label>
              <input style={inputStyle} type="text" value={emergForm.reason} onChange={eUpd('reason')} placeholder="Working from off-site location…" />
            </div>
          </form>
        </ModalShell>
      )}

      <Toast message={toast.message} type={toast.type} onClose={clearToast} />
    </div>
  );
}